export const ACTIVITY_SHOW_TABS = ['general'] as const;

export type ActivityShowTab = (typeof ACTIVITY_SHOW_TABS)[number];

export const ACTIVITY_DEFAULT_TAB: ActivityShowTab = 'general';

/**
 * The audit verbs the backend emits, from the `AuditLog` subclasses in each
 * module's `audit_logs.py`. A curated list, not a query scope: the column
 * filter offers these, and a verb added on the backend before this list
 * catches up is still reachable through the search box, which prefix-matches
 * `verb`.
 *
 * Sorted alphabetically so the dotted namespace groups the options on its own.
 */
export const ACTIVITY_VERBS: readonly string[] = [
  'content_catalog_publisher.delivery_account_recorded',
  'content_catalog_publisher.delivery_activated',
  'content_catalog_publisher.delivery_configured',
  'content_catalog_publisher.delivery_deactivated',
  'master_file.retention_purged',
  'master_file.retention_started',
  'oauth_account.link_rejected',
  'oauth_account.linked',
  'oauth_account.unlink_rejected',
  'oauth_account.unlinked',
  'profile.avatar_changed',
  'profile.favorite_categories_changed',
  'profile.favorite_collections_changed',
  'profile.formats_changed',
  'profile.renamed',
  'profile.settings_changed',
  'publication.early_access_cancelled',
  'publication.early_access_scheduled',
  'publication.import_applied',
  'publication.mod_status_changed',
  'publication.updated',
  'review.accepted',
  'review.created',
  'review.deleted',
  'review.rejected',
  'review.reported',
  'review.updated',
  'revenuecat_billing_period.given_precedence',
  'royalty_report.approved',
  'royalty_report.rejected',
  'royalty_report.sent',
  'royalty_report.skipped',
  'sample.created',
  'settlement.group_reset',
  'user.account_deleted',
  'user.blocked',
  'user.email_changed',
  'user.email_verified',
  'user.force_verified',
  'user.login',
  'user.login_failed',
  'user.logout',
  'user.password_changed',
  'user.password_reset',
  'user.password_reset_requested',
  'user.password_verification_failed',
  'user.registered',
  'user.role_changed',
  'user.unblocked',
  'user.upgraded_from_anonymous',
  'user.verification_requested',
];

/** Model names the audit logs use as `object_type` / `target_type`. */
export const ACTIVITY_OBJECT_TYPES: readonly string[] = [
  'ContentCatalogPublisher',
  'Entitlement',
  'NovelistDeliveryConfiguration',
  'OAuthAccount',
  'OAuthToken',
  'Profile',
  'Publication',
  'RevenueCatBillingPeriod',
  'Review',
  'RoyaltyReport',
  'S3ObjectReference',
  'Sample',
  'SettlementGroup',
  'User',
];

/**
 * Subject/target model name → the Refine resource that shows it.
 *
 * Only the types with a `show` route are here; the rest render as plain text.
 * `ShowIconLink` degrades on its own when a resource cannot resolve a path,
 * but skipping the lookup keeps the intent visible.
 */
export const ACTIVITY_SUBJECT_RESOURCE: Record<string, string> = {
  ContentCatalogPublisher: 'content_catalog.publishers',
  Entitlement: 'monetization.entitlements',
  OAuthAccount: 'iam.oauth_accounts',
  OAuthToken: 'iam.oauth_tokens',
  Profile: 'account.profiles',
  Publication: 'user_catalog.publications',
  RevenueCatBillingPeriod: 'monetization.revenuecat_billing_periods',
  Review: 'feedback.reviews',
  RoyaltyReport: 'settlement.royalty_reports',
  User: 'iam.users',
};
